/** Dialog 'Nova ausência' / 'Editar ausência': Atendente* + Período* + Descrição. */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Absence, AbsenceBody } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Dialog } from '@/components/ui/misc';
import { teamListQuery } from '@/features/team/team-queries';
import { errorMessage } from './agenda-queries';
import { useCreateAbsence, useUpdateAbsence } from './agenda-mutations';
import { toISODay } from './agenda-format';

export function AbsenceDialog({
  initial,
  onClose,
}: {
  initial?: Absence;
  onClose: () => void;
}) {
  // a equipe ativa deste tenant (pedido da PoolTrack, 05/10)
  const usersQ = useQuery(teamListQuery());
  const activeUsers = usersQ.data?.items ?? [];

  const [userId, setUserId] = useState(initial ? String(initial.userId) : '');
  const [startDate, setStartDate] = useState(initial?.startDate ?? toISODay(new Date()));
  const [endDate, setEndDate] = useState(initial?.endDate ?? toISODay(new Date()));
  const [description, setDescription] = useState(initial?.description ?? '');

  const create = useCreateAbsence();
  const update = useUpdateAbsence(initial?.id ?? 0);
  const mutation = initial ? update : create;

  const valid = userId !== '' && startDate !== '' && endDate !== '' && startDate <= endDate;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const body: AbsenceBody = {
      userId: Number(userId),
      startDate,
      endDate,
      description: description.trim() || null,
    };
    mutation.mutate(body, { onSuccess: onClose });
  };

  return (
    <Dialog open onClose={onClose} title={initial ? 'Editar ausência' : 'Nova ausência'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="absence-user">
            Atendente <span className="text-destructive">*</span>
          </Label>
          <Select
            id="absence-user"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
          >
            <option value="" disabled>
              Selecione o atendente…
            </option>
            {activeUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>
            Período <span className="text-destructive">*</span>
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-label="Início do período"
              required
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              aria-label="Fim do período"
              required
            />
          </div>
          {startDate && endDate && startDate > endDate ? (
            <p className="text-xs text-destructive">O fim deve ser igual ou depois do início.</p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="absence-description">Descrição</Label>
          <Textarea
            id="absence-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {mutation.error ? (
          <p className="text-sm text-destructive">{errorMessage(mutation.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={mutation.isPending || !valid}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
